import type { InternalAxiosRequestConfig } from "axios";
import { config } from "../Constants";

/**
 * Cookie-session auth fallback for embedded sub-routes.
 *
 * When My Site is embedded in a host that already holds an authenticated cookie
 * session with the enterprise backend (e.g. a mobile app's webview), token-based
 * SSO (MSAL) may be unavailable. This module lets a request fall back to that
 * cookie session instead of failing outright.
 *
 * Pipeline: Probe -> Cache -> Branch -> Invalidate (on 401).
 *
 * We prefer token auth everywhere; this is only ever consulted after token
 * acquisition has already failed, and only while running in the embedded context.
 */

// The enterprise "who am I" endpoint. It is authenticated, side-effect-free, and
// validates the cookie session server-side — a successful response is the only
// proof the session is valid (a cookie merely existing is not). Point this at a
// dedicated /auth/session endpoint if one is ever added.
const SESSION_CHECK_URL = `${config.url.API_URL}/auth/session`;

let cachedResult: boolean | undefined;
let pendingCheck: Promise<boolean> | undefined;
let cookieAuthRejected = false;

/**
 * Set on any request that went out on the cookie transport, so a later 401 can be
 * attributed to cookie auth rather than to an expired token.
 */
const COOKIE_TRANSPORT_FLAG = "__cookieSessionTransport";

/** True when this request was sent on the cookie transport. */
export function usedCookieTransport(requestConfig: any): boolean {
  return Boolean(requestConfig?.[COOKIE_TRANSPORT_FLAG]);
}

/**
 * Call when the backend answers 401 to a cookie-transport request. That is the
 * server stating it will not authenticate this caller from the cookie — either it
 * never received one, or the endpoint requires a bearer token (`WWW-Authenticate:
 * Bearer`). A 2xx from the session probe is not proof against this, so treat the
 * 401 as authoritative and stop attempting the cookie transport for this page load.
 */
export function markCookieSessionRejected(): void {
  cookieAuthRejected = true;
  clearCookieSessionCache();
}

/** Strips the cookie transport from a config so it can be retried on token auth. */
export function stripCookieTransport<T extends { withCredentials?: boolean }>(requestConfig: T): T {
  const next: any = { ...requestConfig };
  delete next[COOKIE_TRANSPORT_FLAG];
  next.withCredentials = false;
  return next;
}

/**
 * True only when running inside an embedded sub-route. Embedded views are served
 * under the `#/embedded-app/` hash route (see App.tsx). Gating the probe behind
 * this keeps it a no-op cost for every normal standalone visit.
 */
export function isEmbeddedContext(): boolean {
  return (
    typeof window !== "undefined" &&
    window.location.hash.startsWith("#/embedded-app/")
  );
}

/**
 * Probes the enterprise backend for an existing cookie session.
 *
 * Returns false immediately outside the embedded context (no wasted round-trip).
 * Inside it, the result is cached for the page's lifetime and concurrent callers
 * share one in-flight request. Fails closed — any network error or non-2xx
 * response resolves to false and never throws.
 */
export async function hasCookieSession(): Promise<boolean> {
  if (!isEmbeddedContext()) return false;
  if (cookieAuthRejected) return false;
  if (cachedResult !== undefined) return cachedResult;
  if (pendingCheck) return pendingCheck;

  pendingCheck = (async () => {
    try {
      const response = await fetch(SESSION_CHECK_URL, {
        credentials: "include",
      });
      cachedResult = response.ok;
      return cachedResult;
    } catch {
      cachedResult = false;
      return false;
    } finally {
      pendingCheck = undefined;
    }
  })();

  return pendingCheck;
}

/**
 * Call after a 401 so the next hasCookieSession() re-verifies instead of trusting
 * a stale "yes" forever (e.g. the host's session expired mid-visit).
 */
export function clearCookieSessionCache(): void {
  cachedResult = undefined;
  pendingCheck = undefined;
}

/**
 * Enterprise endpoints the embedded views depend on, either directly or through
 * their contexts. Requests to these prefer the host's cookie session over a bearer
 * token (see tryCookieFirst). Matched by path prefix, ignoring the api base url and
 * any query string, so "mysite/suggestions" also covers "mysite/suggestions/7/dismiss".
 */
const COOKIE_FIRST_PATHS = [
  // Identity, shared by every embedded view (CurrentUserContext)
  "me", // current user (and profile picture)
  "mysite/accesscontrol", // permissions
  "sharepoint/userprofiles", // profile properties (read and update)

  // Openings
  "staffing/matches", // matched clients - the list and detail
  "clients", // client fields for the opening detail (the match only carries ids)
  "staffing/process", // express interest

  // Notifications
  "mysite/suggestions", // the list, single item ($filter) and dismiss
  "procedures/execute", // notification card actions (executeProc)
  "sharepoint/lists/items", // notification form submissions
  "sharepoint/documents", // notification form file uploads
];

/**
 * Reduces a request url to a comparable api path:
 * "https://host/api/staffing/process?x=1" -> "staffing/process".
 */
function toApiPath(url: string | undefined): string {
  if (!url) return "";
  const base = config.url.API_URL;
  const withoutBase = url.startsWith(base) ? url.slice(base.length) : url;
  return withoutBase
    .split("?")[0]
    .replace(/^\/+/, "")
    .replace(/\/+$/, "")
    .toLowerCase();
}

/** True when this url is one of the endpoints that should prefer the cookie session. */
export function prefersCookieSession(url: string | undefined): boolean {
  const path = toApiPath(url);
  if (!path) return false;
  return COOKIE_FIRST_PATHS.some(
    (allowed) => path === allowed || path.startsWith(`${allowed}/`),
  );
}

/**
 * Cookie-FIRST for the endpoints the embedded Openings views use: when the host
 * holds a valid cookie session, ride it instead of asking MSAL for a bearer token
 * at all. The webview may have no MSAL account (or a stale one), and attempting
 * token auth there can trigger interactive login the host cannot show.
 *
 * Returns null - meaning "carry on with normal token auth" - outside the embedded
 * context, for any endpoint not on the allowlist, or when no cookie session exists.
 */
export async function tryCookieFirst(
  requestConfig: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig | null> {
  if (!isEmbeddedContext()) return null;
  if (!prefersCookieSession(requestConfig.url)) return null;
  return tryCookieFallback(requestConfig);
}

/**
 * If a valid cookie session exists, switch the request onto the cookie transport
 * (send credentials, drop any bearer header) and return it. Otherwise return null
 * so the caller can proceed with its normal "no auth available" handling.
 *
 * Use this at the points where token auth has failed, e.g.:
 *
 *   const cookieConfig = await tryCookieFallback(config);
 *   if (cookieConfig) return cookieConfig;
 *   return Promise.reject(err); // existing behavior
 */
export async function tryCookieFallback(
  requestConfig: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig | null> {
  if (!(await hasCookieSession())) return null;

  requestConfig.withCredentials = true;
  (requestConfig as any)[COOKIE_TRANSPORT_FLAG] = true;
  if (requestConfig.headers) {
    delete requestConfig.headers.Authorization;
  }
  return requestConfig;
}
